/* Advent product pages (sections/sl-pdp-*.liquid) and the Shop page's tiles. Loaded once, deferred, from
   layout/sl-advent.liquid (fix wave 1, A23: never from section markup); runs once per page (guarded) and works by event
   delegation, so sections the theme editor adds or re-renders need nothing re-run.
   - Add to cart: a product form marked data-sl-add (single boxes, "Looking for something else?" tiles) is posted to
     /cart/add.js instead of navigating; without JS it posts to /cart/add as a normal form.
   - Bundles: a [data-sl-bundle-add] button posts every component variant and the gift in ONE /cart/add.js, each line
     with the bundle cart contract's properties (CONTRACTS.md, "Advent bundles in the cart"). The shell product is
     never added. The data comes from the section's data-sl-* attributes (rendered from the bundle metafields).
   - On success the cart drawer opens with the new lines (CONTRACTS.md "Advent bundles in the cart", writers): the
     request asks for the drawer's sections and the response goes to the drawer's renderContents(), which swaps them
     in and opens it. Our drawer first (window.SLCart: sectionIds(), renderContents(state, { opener })), else Dawn's
     <cart-drawer> (getSectionsToRender(), renderContents(state)); Dawn's pubsub cartUpdate is published too. No
     drawer: go to the cart page. On failure the cart's own message shows under the button.
   - Gallery: the "1 / 5" count follows the swipe (the dots are sl-advent.js's); from 750px the previous and next
     buttons scroll it by one image.
   - Sticky bar (phones, advent/MOBILE-V2.md default 1): an IntersectionObserver on the in-page Pre-Order
     ([data-sl-buybar-anchor]) sets data-sl-shown on .sl-buybar while that button is above the screen, and removes it
     when the button comes back. The CSS slides the bar in (transform, opacity) and only then pads the page's foot.
     Without IntersectionObserver or JS the bar never shows; the in-page button still works. Re-observed when the theme
     editor reloads the section.
   - Bundles are single products since 16 Oct (Matt): their page uses the product form above. The component-lines path
     runs only where the section renders [data-sl-bundle-add], i.e. with the bundle_components metafield set.
   window.slPdp.bundleItems and bundleFromDataset are pure, for test/sl-pdp.test.js. */
(() => {
  if (window.slPdp) return;

  // Lines for one added bundle. Repeats of a variant become one line with its quantity.
  function bundleItems({ productId, bundle, name, shipNote, components = [], gift = null, now = Date.now() }) {
    const bundleId = `${productId}-${now}`;
    const props = (role) => {
      const p = { _BundleId: bundleId, _Bundle: bundle, _BundleName: name, _BundleRole: role };
      if (shipNote) p.PreOrder = shipNote;
      return p;
    };
    const items = [];
    for (const id of components) {
      const line = items.find((i) => i.id === id);
      if (line) line.quantity += 1;
      else items.push({ id, quantity: 1, properties: props('component') });
    }
    if (items.length && gift) items.push({ id: gift, quantity: 1, properties: props('gift') });
    return items;
  }

  function bundleFromDataset(d) {
    const num = (v) => (v ? Number(v) : null);
    return {
      productId: num(d.slProductId),
      bundle: d.slBundle || '',
      name: d.slName || '',
      shipNote: d.slShipNote || '',
      components: (d.slComponents || '').split(',').filter(Boolean).map(Number),
      gift: num(d.slGift),
    };
  }

  window.slPdp = { bundleItems, bundleFromDataset };
  if (typeof document === 'undefined') return;

  // Routes come from the layout (window.routes, locale-aware), never written here.
  const routes = () => window.routes || {};
  const addUrl = () => `${routes().cart_add_url}.js`;
  const drawer = () => document.querySelector('cart-drawer');

  function drawerSections() {
    if (window.SLCart && typeof window.SLCart.sectionIds === 'function') return window.SLCart.sectionIds();
    const cart = drawer();
    return cart && typeof cart.getSectionsToRender === 'function' ? cart.getSectionsToRender().map((s) => s.id) : null;
  }

  async function post(payload) {
    const found = drawerSections();
    const sections = found && found.length ? found : null;
    let init;
    if (payload instanceof FormData) {
      if (sections) {
        payload.append('sections', sections.join(','));
        payload.append('sections_url', window.location.pathname);
      }
      init = { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: payload };
    } else {
      const body = sections ? { ...payload, sections, sections_url: window.location.pathname } : payload;
      init = { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) };
    }
    const res = await fetch(addUrl(), init);
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      throw new Error(data.description || data.message || (window.cartStrings && window.cartStrings.error) || 'Error');
    }
    return data;
  }

  function opened(data, opener) {
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'sl-pdp', cartData: data });
    }
    if (window.SLCart && typeof window.SLCart.renderContents === 'function') {
      window.SLCart.renderContents(data, { open: true, opener });
      return;
    }
    const cart = drawer();
    if (cart && typeof cart.renderContents === 'function') {
      cart.classList.remove('is-empty');
      cart.renderContents(data);
    } else if (cart && typeof cart.open === 'function') {
      cart.open();
    } else if (routes().cart_url) {
      window.location.href = routes().cart_url;
    }
  }

  // The error lines of the area a button belongs to (the section for the buy box and bar, the tile's form).
  function errorsFor(button) {
    const scope = button.closest('form[data-sl-add]:not(.sl-pdp__form)') || button.closest('[data-sl-pdp]') || (button.form && button.form.closest('[data-sl-pdp]')) || button.form;
    return scope ? [...scope.querySelectorAll('[data-sl-pdp-error]')] : [];
  }

  // Free gift (Joey 20 Oct, L2: one free Binder with the first Nativity Bundle). A form holding a nameless
  // input[data-sl-gift-variant] adds that
  // variant with it, once: only when the cart holds none yet. The gift carries the product's PreOrder line (it ships with
  // it, so no mixed-cart note) and a hidden _sl_gift marker. Shopify's automatic Buy X Get Y discount makes it free.
  async function withGift(form) {
    const data = new FormData(form);
    const gift = form.querySelector('[data-sl-gift-variant]')?.value;
    if (!gift) return data;
    try {
      const res = await fetch(`${routes().cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      if ((cart.items || []).some((i) => String(i.variant_id) === gift)) return data;
    } catch {
      return data;
    }
    const properties = {};
    for (const [key, value] of data) {
      const m = /^properties\[(.+)\]$/.exec(key);
      if (m) properties[m[1]] = value;
    }
    const giftProps = { _sl_gift: '1' };
    if (properties.PreOrder) giftProps.PreOrder = properties.PreOrder;
    return { items: [
      { id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1), properties },
      { id: Number(gift), quantity: 1, properties: giftProps },
    ] };
  }

  // With Upcart on (the live cart drawer), Upcart takes the add form's submit and adds the product itself, so withGift
  // never runs (Joey 22 Oct). The page then keeps the gift in step with Upcart's cart updates instead: for each add form
  // holding a gift (the rule: that form's variant -> the gift variant), a cart with the product and no gift gets one
  // (PreOrder from the product's line, _sl_gift); a marked gift left without the product is taken out (it would cost
  // full price). Then Upcart refreshes its drawer. One sync at a time; Upcart's next update re-checks.
  const giftRules = () => [...document.querySelectorAll('form[data-sl-add]')]
    .map((f) => ({ trigger: f.querySelector('input[name="id"]')?.value, gift: f.querySelector('[data-sl-gift-variant]')?.value }))
    .filter((r, i, all) => r.trigger && r.gift && all.findIndex((x) => x.trigger === r.trigger && x.gift === r.gift) === i);
  let syncing = false;
  async function syncGifts() {
    const rules = giftRules();
    if (!rules.length || syncing) return;
    syncing = true;
    try {
      const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
      const cart = await (await fetch(`${routes().cart_url}.js`, { headers: { Accept: 'application/json' } })).json();
      const items = cart.items || [];
      let changed = false;
      for (const rule of rules) {
        const product = items.filter((i) => String(i.variant_id) === rule.trigger);
        const gifts = items.filter((i) => String(i.variant_id) === rule.gift);
        if (product.length && !gifts.length) {
          const properties = { _sl_gift: '1' };
          const note = product[0].properties && product[0].properties.PreOrder;
          if (note) properties.PreOrder = note;
          await fetch(addUrl(), { method: 'POST', headers: json, body: JSON.stringify({ items: [{ id: Number(rule.gift), quantity: 1, properties }] }) });
          changed = true;
        } else if (!product.length) {
          for (const line of gifts.filter((i) => i.properties && i.properties._sl_gift)) {
            await fetch(`${routes().cart_change_url || '/cart/change'}.js`, { method: 'POST', headers: json, body: JSON.stringify({ id: line.key, quantity: 0 }) });
            changed = true;
          }
        }
      }
      if (changed && typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
    } catch {
      // the next cart update tries again
    } finally {
      syncing = false;
    }
  }
  function watchUpcart(tries = 0) {
    if (typeof window.upcartSubscribeCartUpdated === 'function') {
      window.upcartSubscribeCartUpdated(() => syncGifts());
      syncGifts();
    } else if (tries < 20) {
      setTimeout(() => watchUpcart(tries + 1), 500); // Upcart's script can arrive after this one
    }
  }
  if (giftRules().length) watchUpcart();

  async function run(buttons, errors, payload, opener) {
    if (buttons.some((b) => b.hasAttribute('aria-busy'))) return;
    errors.forEach((e) => { e.hidden = true; e.textContent = ''; });
    buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
    try {
      opened(await post(typeof payload === 'function' ? await payload() : payload), opener);
    } catch (err) {
      errors.forEach((e) => { e.textContent = err.message; e.hidden = false; });
    } finally {
      buttons.forEach((b) => b.removeAttribute('aria-busy'));
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-sl-add]');
    if (!form || !window.fetch) return;
    event.preventDefault();
    const buttons = [...document.querySelectorAll(`[form="${form.id}"]`), ...form.querySelectorAll('button[type="submit"]')];
    const opener = event.submitter || buttons[0] || form;
    run(buttons, errorsFor(opener), () => withGift(form), opener);
  });

  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-sl-gallery-step]');
    if (step) {
      const row = step.parentElement.querySelector('[data-sl-gallery]');
      const width = row && row.firstElementChild ? row.firstElementChild.getBoundingClientRect().width : 0;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (row) row.scrollBy({ left: Number(step.dataset.slGalleryStep) * width, behavior: reduce ? 'auto' : 'smooth' });
      return;
    }
    const button = event.target.closest('[data-sl-bundle-add]');
    if (!button) return;
    const root = button.closest('[data-sl-pdp]');
    if (!root) return;
    const items = bundleItems(bundleFromDataset(root.dataset));
    if (!items.length) return;
    run([...root.querySelectorAll('[data-sl-bundle-add]')], errorsFor(button), { items }, button);
  });

  function watchBar(root) {
    const bar = root.querySelector('.sl-buybar');
    const anchor = root.querySelector('[data-sl-buybar-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    if (root.__slBarObserver) root.__slBarObserver.disconnect();
    // The root reaches far below the screen, so the button counts as "in" until it passes the top edge: one state
    // change however the page moves (a fling or a jump never skips it), and none on the way down to the button.
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      const top = entry.rootBounds ? entry.rootBounds.top : 0;
      const shown = !entry.isIntersecting && entry.boundingClientRect.height > 0 && entry.boundingClientRect.bottom <= top;
      bar.toggleAttribute('data-sl-shown', shown);
    }, { rootMargin: '0px 0px 100000px 0px' });
    observer.observe(anchor);
    root.__slBarObserver = observer;
  }
  document.querySelectorAll('[data-sl-pdp]').forEach(watchBar);
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-sl-pdp]');
    if (root) watchBar(root);
  });

  document.addEventListener(
    'scroll',
    (event) => {
      const row = event.target;
      if (!(row instanceof Element) || !row.matches('[data-sl-gallery]')) return;
      const count = row.parentElement.querySelector('[data-sl-count]');
      if (!count) return;
      const step = row.firstElementChild ? row.firstElementChild.getBoundingClientRect().width : 1;
      const max = row.scrollWidth - row.clientWidth;
      const index = row.scrollLeft >= max - 2 ? row.children.length - 1 : Math.round(row.scrollLeft / (step || 1));
      count.textContent = String(index + 1);
    },
    { capture: true, passive: true }
  );
})();
